import {
    Key,
    memo,
    ReactElement,
    ReactNode,
    useEffect,
    useMemo,
    useState,
} from 'react'

import { Button, Skeleton } from 'antd'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'
import type { MenuInfo } from 'rc-menu/es/interface'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { makeUpdateFilter, makeUpdateLocalFilter } from 'actions/ui/shared'
import type { HulkQueryResponseTimeRangeMeta } from 'api/hulkApi'
import { ContentCard } from 'components/ContentCard'
import { Flex, Icon } from 'components/core'
import { Empty } from 'components/Empty'
import { makeMapping } from 'configuration/dataSources/helpers'
import { DATA_SOURCES } from 'configuration/widgets'
import { DASHBOARD_ACTIONS } from 'const/dashboards'
import { DATES, RANGE_LAG } from 'const/filters'
import { DEFAULT_TABLE_WIDGET_PAGE_SIZE } from 'const/tables'
import { CATEGORY_CHART, METRIC, TABLE, TEXT, TIME_SERIES } from 'const/widgets'
import { usePageContext } from 'context/PageContext'
import { InfoRow } from 'features/Dashboards/components/Widgets/InfoRow'
import { InfoRowStyle } from 'features/Dashboards/components/Widgets/InfoRow/InfoRow'
import { derivesRangeLag, widgetRangeLag } from 'features/Dashboards/rangeLag'
import {
    useGetDataSourceGroupBysColumns,
    useGetDataSourceMetricsColumns,
    useGetDataSources,
} from 'features/MetricsGlossary/queries'
import {
    DatadogActionName,
    useMonitorLoadTime,
} from 'features/Monitoring/Datadog'
import {
    getDateRangeInDays,
    isWholeWeekWindow,
    ResolvedDateWindow,
} from 'helpers/dateRange'
import { userHasCustomerServicePermissions } from 'helpers/featurePermissions'
import {
    formatFilterHierarchy,
    getFiltersHierarchy,
} from 'helpers/filters/dashboards'
import { useUiDomainValue, useUserHasPermissions } from 'hooks'
import { PageId } from 'routing/pages'
import { CobaltPath } from 'routing/Path'
import {
    selectLocalFilters,
    selectVisibleCombinedFilters,
    selectVisibleFilters,
    selectWidgetTab,
} from 'selectors/ui'
import {
    CurrencyCode,
    DashboardAction,
    DashboardTab,
    DateRangeFilter,
    FiltersState,
    FilterValue,
    RootReduxState,
    Widget as IWidget,
    WidgetFilterMap,
} from 'types'
import type { DataWidgetConfig } from 'types/dashboards'
import type { DateGrain } from 'types/dataSources'
import { ActionsDropdown } from 'views/DashboardPage/Actions'

import CategoryChartWidget from './CategoryChartWidget/CategoryChartWidget'
import MetricWidget from './MetricWidget/MetricWidget'
import {
    useGetWidgetDataQuery,
    useGetWidgetPriorPeriodDataQuery,
    usesReportedComparison,
} from './queries'
import * as styles from './styles.scss'
import TableWidget from './TableWidget/TableWidget'
import TextWidget from './TextWidget/TextWidget'
import TimeSeriesWidget from './TimeSeriesWidget/TimeSeriesWidget'
import { useMarketAnalysisSegmentChipLabels } from './useMarketAnalysisSegmentChipLabels'
import WidgetConfigExplanation from './WidgetConfigExplanation'

// Set to longer period for now.
// Sales calls often preload dashboards and leave them open for a while.
// We may be able to reduce this if we more manually control caching for dashboards
// to avoid invalidating cache for dashboard tabs that are unmounted while the dashboard is still mounted.
const WIDGET_CACHE_TIME = 1000 * 60 * 60 * 4

// Dev-only standing check. Every date on this widget — the chip, the table
// headers, the chart axis, the prior series — is read from the window the server
// reports, so if the server stops aligning that window they all keep agreeing
// with each other and quietly disagree with the data. No user-facing surface can
// catch that, and neither can a test that mocks the response.
function warnOnUnalignedWindow(
    dataSource: string,
    timeRange: HulkQueryResponseTimeRangeMeta,
    expectsComparison: boolean,
    grain?: DateGrain
): void {
    if (process.env.NODE_ENV === 'production' || grain?.unit !== 'week') {
        return
    }

    // The prior series has one source now, so a comparison that was asked for and
    // not reported means no series at all. Deliberately not filled in from a
    // client-side guess — that is the second definition this phase removed — so
    // the only way to notice is to say it here.
    if (expectsComparison && !timeRange.comparison_start) {
        console.error(
            `${dataSource}: change metrics are on but the response reported no comparison window, ` +
                'so the widget has no prior period to plot.'
        )
    }

    const unaligned = [
        ['window', timeRange.start, timeRange.end],
        ['comparison', timeRange.comparison_start, timeRange.comparison_end],
    ].filter(([, start, end]) => start && end && !isWholeWeekWindow(start, end))

    unaligned.forEach(([which, start, end]) => {
        console.error(
            `${dataSource}: reported ${which} ${start}..${end} is not whole Sun–Sat weeks. ` +
                'Check that the request sets align_to_grain and that hulk falls back to the native grain.'
        )
    })
}

function useFilterConfiguration(
    pageName: PageId,
    widget: IWidget,
    dateRange?: DateRangeFilter,
    rangeLag?: FilterValue
): WidgetFilterMap {
    const path: CobaltPath = [pageName]
    const pageFilters = useSelector<RootReduxState, Partial<FiltersState>>(
        (state) => selectVisibleFilters(state, path)
    )
    const pageLocalFilters = useSelector<RootReduxState, Partial<FiltersState>>(
        (state) => selectLocalFilters(state, path)
    )
    const tab = useSelector<
        RootReduxState,
        | {
              [key: string]: any
              widgets: IWidget[]
          }
        | undefined
    >((state) => selectWidgetTab(state, pageName, widget.id))

    const filters: FiltersState = useSelector<RootReduxState, FiltersState>(
        (state) =>
            selectVisibleCombinedFilters(state, [pageName, 'tabs', tab?.id])
    )
    const dispatch = useDispatch()

    // Cleaning up old page filters since they aren't needed anymore
    // After DOW-10221, the correct filters now come from tabs
    if (pageName === 'dashboardPage' || pageName === 'homePage') {
        if (!isEmpty(pageFilters?.[DATES])) {
            const updateFilter = makeUpdateFilter(path)
            dispatch(updateFilter({ key: DATES, value: [] }))
        }

        if (!isEmpty(pageLocalFilters?.[DATES])) {
            const updateFilter = makeUpdateLocalFilter(path)
            dispatch(updateFilter({ key: DATES, value: [] }))
        }
    }

    // Something is sketchy with these types
    // FiltersState != WidgetFilterMap but this code uses them interchangeably
    const filterMap = filters as WidgetFilterMap

    const { result: hierarchy } = getFiltersHierarchy(
        filterMap,
        widget.filters,
        widget.data_source
    )

    hierarchy[DATES] = {
        ...hierarchy[DATES],
        values: dateRange ?? hierarchy[DATES]?.values,
    }
    hierarchy[RANGE_LAG] = {
        ...hierarchy[RANGE_LAG],
        values: rangeLag ?? hierarchy[RANGE_LAG]?.values,
    }

    Object.keys(hierarchy).forEach((filter) => {
        filterMap[filter] = hierarchy[filter].values
    })

    return filterMap
}

interface SelectedPage {
    rowSetKey: string
    number: number
}

interface Props {
    widget: IWidget
    widgetTab?: DashboardTab
    otherTabs?: DashboardTab[]
    className?: string
    isExport?: boolean
    hasPermission: boolean
    showFiltersDisplay?: boolean
    dateRange?: DateRangeFilter
    rangeLag?: FilterValue
    onEdit?: (widget: IWidget) => void
    updateWidget?: (widget: IWidget) => void
    onDelete?: (widget: IWidget) => void
    onClone?: (widget: IWidget, tabId?: Key) => void
    onExport?: (widget: IWidget) => void
    onCloneToDashboard?: (widget: IWidget) => void
}

function Widget({
    widget,
    widgetTab,
    otherTabs = [],
    hasPermission,
    dateRange,
    rangeLag,
    updateWidget = noop,
    onEdit = noop,
    onDelete = noop,
    onClone = noop,
    onExport = noop,
    onCloneToDashboard = noop,
    className = '',
    isExport = false,
    showFiltersDisplay = false,
}: Props): ReactElement {
    const { t } = useTranslation(['dashboards', 'common'])
    const [selectedPage, setSelectedPage] = useState<SelectedPage>({
        rowSetKey: '',
        number: 1,
    })
    const { pageName } = usePageContext()
    const pageCurrencyCode = useUiDomainValue<CurrencyCode>([
        pageName,
        'currencyCode',
    ])
    const appCurrencyCode = useUiDomainValue<CurrencyCode>([
        'app',
        'currencyCode',
    ])

    const { result: hierarchy } = getFiltersHierarchy(
        widgetTab?.localFilters || {},
        widget.filters,
        widget.data_source
    )

    // Always use the date range override if present
    hierarchy[DATES] = {
        ...hierarchy[DATES],
        values: dateRange ?? hierarchy[DATES]?.values,
    }
    // On a saved dashboard the lag comes from the data sources, worked out here
    // before the first query, so the widget fetches once and a stale saved lag
    // is never read. Memoized on the tab's widgets: the walk over them is the
    // same for every render until they change.
    const resolvedDates = hierarchy[DATES]?.values
    const derivedRangeLag = useMemo(
        () =>
            derivesRangeLag(pageName)
                ? widgetRangeLag(widget, widgetTab?.widgets, resolvedDates)
                : undefined,
        [pageName, widget, widgetTab?.widgets, resolvedDates]
    )
    const effectiveRangeLag: FilterValue | undefined =
        rangeLag ?? (derivedRangeLag as unknown as FilterValue | undefined)
    hierarchy[RANGE_LAG] = {
        ...hierarchy[RANGE_LAG],
        values: effectiveRangeLag ?? hierarchy[RANGE_LAG]?.values,
    }

    const chipLabelOptions = useMarketAnalysisSegmentChipLabels(
        widget,
        hierarchy
    )
    const filters: WidgetFilterMap = Object.fromEntries(
        Object.entries(hierarchy).map(([filter, result]) => [
            filter,
            result.values,
        ])
    )

    const currencyOverride = DATA_SOURCES[
        widget.data_source
    ]?.getCurrencyOverride?.(widget, filters)
    const tabCurrencyCode = widgetTab?.currency_code
    const currencyCode =
        currencyOverride ||
        tabCurrencyCode ||
        pageCurrencyCode ||
        appCurrencyCode
    // useCustomCurrencyCode already points numeral's locale at the page-level
    // currency, so a child only needs a code when the effective one differs.
    const displayCurrencyCode = currencyOverride || tabCurrencyCode || undefined
    const filterConfiguration = useFilterConfiguration(
        pageName,
        widget,
        dateRange,
        effectiveRangeLag
    )

    // Rebuilt every render: useFilterConfiguration mutates a redux object in
    // place, so its identity does not track its contents.
    const rowSetKey = JSON.stringify([
        widget.data_source,
        widget.group_by,
        widget.filters,
        filterConfiguration,
    ])
    const pageNumber =
        selectedPage.rowSetKey === rowSetKey ? selectedPage.number : 1

    const {
        data: widgetDataResponse,
        isLoading: isLoadingWidgetData,
        isRefetching: isRefetchingWidgetData,
        isError: isWidgetDataError,
        error: widgetDataError,
        refetch: refetchWidgetData,
    } = useGetWidgetDataQuery(
        pageName,
        widget,
        filterConfiguration,
        currencyCode,
        widget.type !== TABLE,
        widget.type === TABLE ? pageNumber : undefined,
        widget.type === TABLE
            ? (widget.pagination?.pageSize ?? DEFAULT_TABLE_WIDGET_PAGE_SIZE)
            : undefined,
        {
            gcTime: WIDGET_CACHE_TIME,
        }
    )
    const widgetData = widgetDataResponse?.data.results ?? []
    const totalCount = widgetDataResponse?.data.count

    // Everything the server reported about the window it queried, in the three
    // shapes the widget consumes: the window itself for the chip, the comparison
    // window for the prior-period fetch, and the gap between their starts for the
    // plotted series. Present only for a data source that reports one (today: the
    // weekly hulk-backed ones); until the response lands there is nothing to show
    // and the chip renders the requested range as it always has.
    const resolved = useMemo(() => {
        const timeRange = widgetDataResponse?.data.time_range
        if (!timeRange) {
            return undefined
        }
        const grain = DATA_SOURCES[widget.data_source]?.dateGrain
        warnOnUnalignedWindow(
            widget.data_source,
            timeRange,
            usesReportedComparison(widget) &&
                !!widget.include_change_metrics &&
                widget.type === TIME_SERIES,
            grain
        )
        // One predicate for "the server owns this widget's comparison window", so
        // the gate on the prior-period query and the value it gates cannot answer
        // differently.
        const comparison =
            usesReportedComparison(widget) &&
            timeRange.comparison_start &&
            timeRange.comparison_end
                ? {
                      start: timeRange.comparison_start,
                      end: timeRange.comparison_end,
                  }
                : undefined

        return {
            window: {
                start: timeRange.start,
                end: timeRange.end,
                grain,
            } satisfies ResolvedDateWindow,
            comparison,
            // The same two windows as date pairs, for the consumers that label or
            // plot a range rather than describe it. Built here so their identity is
            // stable per response — the table's column builder is memoized on them.
            range: [timeRange.start, timeRange.end],
            priorRange: comparison && [comparison.start, comparison.end],
            // Both series come from this pair of windows, so the shift that lines
            // them up is the gap between their starts — not a length derived from
            // the picked range, which for weekly data is a different number.
            offsetDays: comparison
                ? getDateRangeInDays(comparison.start, timeRange.start)
                : undefined,
        }
    }, [widgetDataResponse, widget])

    const {
        data: widgetPriorPeriodDataResponse,
        isLoading: isLoadingPriorPeriodWidgetData,
        refetch: refetchPriorPeriodWidgetData,
    } = useGetWidgetPriorPeriodDataQuery(
        pageName,
        widget,
        filterConfiguration,
        currencyCode,
        widget.type !== TABLE,
        widget.type === TABLE ? pageNumber : undefined,
        widget.type === TABLE
            ? (widget.pagination?.pageSize ?? DEFAULT_TABLE_WIDGET_PAGE_SIZE)
            : undefined,
        {
            enabled:
                widget.type === TIME_SERIES && !!widget.include_change_metrics,
            gcTime: WIDGET_CACHE_TIME,
        },
        resolved?.comparison
    )

    useMonitorLoadTime({
        actionName: DatadogActionName.LOAD_WIDGET,
        loading: isLoadingWidgetData,
        widgetContext: {
            type: widget.type,
            name: widget.name,
            data_source: widget.data_source,
            metric_count: widget.metrics.length,
            group_by_count: widget.group_by.length,
            sort_by: {
                field: widget.sorter?.field,
                order: widget.sorter?.order,
            },
            pagination: widget.pagination,
        },
    })

    const refetchData = (): void => {
        refetchWidgetData()
        if (widget.type === TIME_SERIES && widget.include_change_metrics) {
            refetchPriorPeriodWidgetData()
        }
    }

    const widgetPriorPeriodData =
        widgetPriorPeriodDataResponse?.data.results ?? []

    const isImpersonationMode = useUserHasPermissions(
        userHasCustomerServicePermissions
    )

    // Load data sources and metrics for the widget
    // TODO move data sources loading to the parent component
    const { data: dataSourceData, isLoading: isLoadingDataSources } =
        useGetDataSources({
            includeEndOfLife: true,
        })

    const dataSourceConfig = DATA_SOURCES[widget.data_source]
    const dataSource = (dataSourceData?.data.results ?? []).find(
        (d) => d.metadata_key === dataSourceConfig?.getMetadataKey(widget)
    )
    const dataSourceConfiguration = DATA_SOURCES[widget.data_source]

    const priorPeriodOverride = useMemo(
        () =>
            dataSourceConfiguration?.getPriorPeriodOverride?.(widget, filters),
        [dataSourceConfiguration, widget, filters]
    )
    const resolvedWidget: IWidget = useMemo(
        () =>
            priorPeriodOverride != null
                ? {
                      ...widget,
                      constant_value_for_prior_period: priorPeriodOverride,
                      include_change_metrics: false,
                      period_delta_type: undefined,
                  }
                : widget,
        [priorPeriodOverride, widget]
    )

    const { data: metricsColumnsData, isLoading: isLoadingMetrics } =
        useGetDataSourceMetricsColumns(
            dataSource?.slug,
            dataSourceConfiguration
        )

    const { data: groupByColumnsData, isLoading: isLoadingGroupBys } =
        useGetDataSourceGroupBysColumns(
            dataSource?.slug,
            dataSourceConfiguration
        )

    const metricsColumns = metricsColumnsData ?? []

    const groupByColumns = groupByColumnsData ?? []

    const metricsConfig = useMemo(() => {
        return metricsColumnsData ? makeMapping(metricsColumnsData) : {}
    }, [metricsColumnsData])

    useEffect(() => {
        if (widget.new) {
            // Scroll in next render cycle
            setTimeout(() => {
                const element = document.getElementById(`widget__${widget.id}`)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'end' })
                }
            }, 500) // seems to not work in Chrome without a decent delay
        }
    }, [widget.new, widget.id])

    function renderLoading(): ReactNode {
        // we could extend this to have different loading animations per chart type

        return <Skeleton active />
    }

    function renderWidget(): ReactNode {
        if (isWidgetDataError && !isLoadingWidgetData) {
            // This is not a perfect way to detect a response size issue,
            // as a 502 can be caused by other things, but it's a decent assumption
            // since 502s for other reasons are very rare
            const isTooMuchDataError =
                widgetDataError && widgetDataError?.response?.status === 502

            return (
                <div>
                    {isTooMuchDataError && (
                        <div style={{ textAlign: 'center' }}>
                            <Empty
                                description={t(
                                    'dashboards:widget.loadingError.tooMuchData.header',
                                    'Dataset Too Large'
                                )}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Trans i18nKey="dashboards:widget.loadingError.tooMuchData.description">
                                    We appreciate your thorough input, but it
                                    seems the response is too large for our
                                    system to handle efficiently.
                                    <br />
                                    Try reducing the date range, adjusting the
                                    group bys, slimming down metrics, or adding
                                    some data filters.
                                </Trans>
                            </Empty>
                        </div>
                    )}
                    {!isTooMuchDataError && (
                        <div style={{ textAlign: 'center' }}>
                            {t(
                                'dashboards:widget.loadingError.general',
                                'There was an error loading this widget.'
                            )}
                            <br />
                            <Button
                                type="text"
                                onClick={() => refetchData()}
                                className={styles['retry-btn']}
                            >
                                {t('dashboards:widget.retry', 'Retry')}
                            </Button>
                        </div>
                    )}
                    <WidgetConfigExplanation widget={widget} />
                </div>
            )
        }

        if (widget.type === TEXT) {
            return <TextWidget widget={widget} />
        }

        if (
            widgetData?.length === 0 &&
            !isLoadingWidgetData &&
            !isLoadingPriorPeriodWidgetData
        ) {
            const emptyStateMessage =
                dataSourceConfiguration?.getEmptyStateMessage?.(widget, filters)

            return (
                <WidgetConfigExplanation
                    widget={widget}
                    fallbackToNoData
                    emptyStateMessage={emptyStateMessage}
                />
            )
        }

        if (isLoadingDataSources || isLoadingMetrics || isLoadingGroupBys) {
            return <Skeleton active />
        }

        if (widget.type === METRIC) {
            return (
                <MetricWidget
                    widget={resolvedWidget}
                    widgetData={widgetData}
                    metricsConfig={metricsConfig}
                    currencyCode={displayCurrencyCode}
                />
            )
        }

        const priorPeriodNotice = priorPeriodOverride != null && (
            <div style={{ paddingLeft: 2, marginTop: -12, marginBottom: 4 }}>
                {priorPeriodOverride}
            </div>
        )

        if (widget.type === TABLE) {
            return (
                <div>
                    {priorPeriodNotice}
                    <TableWidget
                        widget={resolvedWidget}
                        widgetData={widgetData}
                        totalCount={totalCount}
                        pageNumber={pageNumber}
                        onPageNumberChange={(page) =>
                            setSelectedPage({ rowSetKey, number: page })
                        }
                        isLoading={
                            isLoadingWidgetData || isRefetchingWidgetData
                        }
                        filters={filters}
                        dataSource={dataSource}
                        metricsColumns={metricsColumns}
                        groupByColumns={groupByColumns}
                        currencyCode={displayCurrencyCode}
                        reportedRange={resolved?.range}
                        reportedPriorRange={resolved?.priorRange}
                    />
                </div>
            )
        }

        if (widget.type === TIME_SERIES) {
            return (
                <div>
                    {priorPeriodNotice}
                    <TimeSeriesWidget
                        widget={resolvedWidget as DataWidgetConfig}
                        widgetData={widgetData}
                        updateWidget={updateWidget}
                        widgetPriorPeriodData={widgetPriorPeriodData}
                        priorPeriodOffsetDays={resolved?.offsetDays}
                        reportedRange={resolved?.range}
                        filters={filters}
                        metricsConfig={metricsConfig}
                        currencyCode={displayCurrencyCode}
                    />
                </div>
            )
        }

        if (widget.type === CATEGORY_CHART) {
            return (
                <div className={styles['category-chart-widget-wrapper']}>
                    {priorPeriodNotice}
                    <CategoryChartWidget
                        widget={resolvedWidget}
                        widgetData={widgetData}
                        metricsConfig={metricsConfig}
                        currencyCode={displayCurrencyCode}
                    />
                </div>
            )
        }

        return null
    }

    function renderCloneSubActions(): DashboardAction[] {
        const subActions = []

        subActions.push({
            label: t('dashboards:Widget.actions.onThisTab', 'On this tab'),
            type: DASHBOARD_ACTIONS.cloneWidgetOnThisTab,
        })

        if (!isEmpty(otherTabs)) {
            subActions.push({
                label: t(
                    'dashboards:Widget.actions.toAnotherTab',
                    'To another tab'
                ),
                type: DASHBOARD_ACTIONS.cloneWidgetToOtherTab,
                subActions: otherTabs.map((tab) => ({
                    label: tab.name,
                    somethingElse: tab.id,
                    type: tab.id,
                })),
            })
        }

        subActions.push({
            label: t(
                'dashboards:Widget.actions.toAnotherDashboard',
                'To another dashboard'
            ),
            type: DASHBOARD_ACTIONS.cloneWidgetToOtherDashboard,
        })

        return subActions
    }

    const acceptedActions = [
        DASHBOARD_ACTIONS.editWidget,
        DASHBOARD_ACTIONS.exportToCsv,
        DASHBOARD_ACTIONS.cloneWidgetOnThisTab,
        DASHBOARD_ACTIONS.cloneWidgetToOtherTab,
        DASHBOARD_ACTIONS.cloneWidgetToOtherDashboard,
        DASHBOARD_ACTIONS.deleteWidget,
        DASHBOARD_ACTIONS.reloadWidget,
    ]

    const handleActionSelected = (e: MenuInfo): void => {
        const actionKey = acceptedActions.includes(e.keyPath[0].toString())
            ? e.keyPath[0]
            : e.keyPath[1]

        switch (actionKey) {
            case DASHBOARD_ACTIONS.editWidget:
                onEdit(widget)
                break

            case DASHBOARD_ACTIONS.exportToCsv:
                onExport(widget)
                break

            case DASHBOARD_ACTIONS.cloneWidgetOnThisTab:
                onClone(widget)
                break

            case DASHBOARD_ACTIONS.cloneWidgetToOtherTab:
                onClone(widget, e.keyPath[0])
                break

            case DASHBOARD_ACTIONS.cloneWidgetToOtherDashboard:
                onCloneToDashboard(widget)
                break

            case DASHBOARD_ACTIONS.deleteWidget:
                onDelete(widget)
                break

            case DASHBOARD_ACTIONS.reloadWidget:
                refetchWidgetData()
                break

            default:
                break
        }
    }

    const hasExportSupport =
        !!DATA_SOURCES[widget.data_source]?.getExportApiFunc

    function renderActions(): ReactElement[] {
        const actions: DashboardAction[] = []

        if (hasPermission) {
            actions.push(
                {
                    label: t('common:edit', 'Edit'),
                    type: DASHBOARD_ACTIONS.editWidget,
                },
                {
                    label: t('common:clone', 'Clone'),
                    type: DASHBOARD_ACTIONS.openCloneSubmenu,
                    subActions: renderCloneSubActions(),
                }
            )

            actions.push({
                label: t('common:delete', 'Delete'),
                type: DASHBOARD_ACTIONS.deleteWidget,
            })
        }

        if (widget.type !== TEXT && hasExportSupport) {
            actions.push({
                label: t(
                    'dashboards:Widget.actions.exportCSV',
                    'Export to CSV'
                ),
                type: DASHBOARD_ACTIONS.exportToCsv,
            })
        }

        if (isImpersonationMode) {
            actions.push({
                label: t('dashboards:Widget.actions.refresh', 'Reload'),
                type: DASHBOARD_ACTIONS.reloadWidget,
            })
        }

        return actions.length
            ? [
                  <ActionsDropdown
                      key="actions"
                      actions={actions}
                      onSelectAction={handleActionSelected}
                      btn={
                          <Flex className={styles['more-icon']}>
                              <Icon
                                  name="DOTS_HORIZONTAL"
                                  color="colorTableHeadSubtitle"
                                  size={16}
                              />
                          </Flex>
                      }
                  />,
              ]
            : []
    }

    function renderFiltersInfo(): ReactNode {
        // If show_filter_row is undefined, widget-level date range filters will still be shown
        // (previously, there was a small footer on the widget showing the applied date range)
        // For widgets created or configured after DOW-10221, this flag will always have a value
        if (
            !isEmpty(widget.filters[DATES]) &&
            widget.show_filter_row === undefined
        ) {
            widget.show_filter_row = true
        }

        if (
            !showFiltersDisplay ||
            widget.type === TEXT ||
            !widget.show_filter_row
        ) {
            return null
        }

        return (
            <InfoRow
                items={formatFilterHierarchy(
                    hierarchy,
                    'This filter overrides the tab-level one.',
                    t,
                    currencyCode,
                    metricsConfig,
                    undefined,
                    chipLabelOptions,
                    resolved?.window
                )}
                styleType={
                    isExport
                        ? InfoRowStyle.PDF_EXPORT
                        : InfoRowStyle.WIDGET_DISPLAY
                }
                onEdit={() => onEdit(widget)}
                showEditWidgetButton={hasPermission}
            />
        )
    }

    const isLoadingInitialData = isLoadingWidgetData || isRefetchingWidgetData
    const showLoading = isLoadingInitialData && widget.type !== TABLE

    return (
        <ContentCard
            id={`widget__${widget.id}`}
            title={widget.name}
            subTitle={widget.description}
            display={renderFiltersInfo()}
            actions={renderActions()}
            showActionsOnHover
            className={classNames(className, styles['filters-info-container'], {
                [styles['is-editable']]: hasPermission,
                [styles['text-widget']]: widget.type === TEXT,
            })}
        >
            {showLoading ? renderLoading() : renderWidget()}
        </ContentCard>
    )
}

export default memo(Widget)
